import React, { useState } from 'react';
import { api } from '../utils/api';
import { useI18n } from '../i18n/I18nProvider';

// AI-bot check form (PIN-328): StatusBot asks the client's support bot one
// question through its API and alerts when it does not answer, or answers
// against the client's instruction. Limits mirror the API (PIN-327).

const HOUR_NS = 3600000000000;

export const AI_BOT_INTERVALS = [
  { labelKey: 'aiBot.intervals.hour1',  ns: HOUR_NS },
  { labelKey: 'aiBot.intervals.hour6',  ns: 6 * HOUR_NS },
  { labelKey: 'aiBot.intervals.hour12', ns: 12 * HOUR_NS },
  { labelKey: 'aiBot.intervals.hour24', ns: 24 * HOUR_NS },
];

const QUESTION_MAX = 300;
const INSTRUCTION_MAX = 500;

const DEFAULT_FORM = {
  url: '',
  interval: 24 * HOUR_NS,
  preset: 'http',
  auth_header: 'Authorization',
  body_template: '{"message": "{{question}}"}',
  answer_path: '',
  model: '',
  question: '',
  instruction: '',
  key: '',
};

const hintStyle = { fontSize: 12, opacity: 0.75, marginTop: 4 };

// t() interpolates {{name}}; feed the template placeholders back in so the hint
// shows them literally instead of blanks.
const PLACEHOLDERS = { question: '{{question}}', uuid: '{{uuid}}' };

// The API answers in English error strings; show the ones a user can act on in
// their language, and the raw text otherwise.
function errorText(code, t) {
  switch (code) {
    case 'ai bot check limit reached': return t('aiBot.errors.limit');
    case 'bot API url points to an internal address': return t('aiBot.errors.internal');
    case 'ai bot checks are not available yet': return t('aiBot.errors.disabled');
    case 'email_not_verified': return t('verifyBanner.text');
    default: return code || t('projectDetail.failedToSaveCheck');
  }
}

const AIBotModal = ({ projectId, check, onClose, onSaved }) => {
  const { t } = useI18n();
  const editing = !!check;
  const [form, setForm] = useState(editing ? {
    ...DEFAULT_FORM,
    url: check.url,
    interval: check.interval || DEFAULT_FORM.interval,
    preset: check.ai_bot?.preset || 'http',
    auth_header: check.ai_bot?.auth_header || DEFAULT_FORM.auth_header,
    body_template: check.ai_bot?.body_template || DEFAULT_FORM.body_template,
    answer_path: check.ai_bot?.answer_path || '',
    model: check.ai_bot?.model || '',
    question: check.ai_bot?.question || '',
    instruction: check.ai_bot?.instruction || '',
  } : DEFAULT_FORM);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [result, setResult] = useState(null); // response of POST …/checks/ai-test

  const set = (key, val) => setForm(f => ({ ...f, [key]: val }));
  const isOpenAI = form.preset === 'openai';

  // An empty key on edit keeps the stored one (the API never returns it).
  const payload = () => ({
    url: form.url.trim(),
    interval: Number(form.interval),
    ai_bot: {
      enabled: true,
      preset: form.preset,
      ...(isOpenAI
        ? { model: form.model }
        : { auth_header: form.auth_header, body_template: form.body_template, answer_path: form.answer_path }),
      question: form.question,
      instruction: form.instruction,
    },
    ai_bot_key: form.key,
  });

  const handleTest = async () => {
    setError('');
    setResult(null);
    setTesting(true);
    const res = await api.testAIBot(projectId, payload(), editing ? check.ID : undefined);
    const d = await res.json().catch(() => ({}));
    setTesting(false);
    if (res.ok) {
      setResult(d);
    } else if (res.status === 429) {
      setError(t('aiBot.errors.rateLimited'));
    } else {
      setError(errorText(d.error, t));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSaving(true);
    const res = editing
      ? await api.updateCheck(projectId, check.ID, payload())
      : await api.createCheck(projectId, payload());
    if (res.ok) {
      onSaved(await res.json());
      onClose();
      return;
    }
    const d = await res.json().catch(() => ({}));
    setError(errorText(d.error, t));
    setSaving(false);
  };

  return (
    <div className="uh-modal-backdrop">
      <div className="uh-modal" onClick={e => e.stopPropagation()}>
        <div className="uh-modal-title">{editing ? t('aiBot.editTitle') : t('aiBot.newTitle')}</div>
        <p style={hintStyle}>{t('aiBot.intro')}</p>
        {error && <div className="uh-alert-error">{error}</div>}
        <form onSubmit={handleSubmit}>
          <div className="uh-form-group">
            <label className="uh-label" htmlFor="aibot-preset">{t('aiBot.preset')}</label>
            <select id="aibot-preset" className="uh-select" value={form.preset} onChange={e => set('preset', e.target.value)}>
              <option value="http">{t('aiBot.presets.http')}</option>
              <option value="openai">{t('aiBot.presets.openai')}</option>
            </select>
          </div>

          <div className="uh-form-group">
            <label className="uh-label" htmlFor="aibot-url">{isOpenAI ? t('aiBot.baseUrl') : t('aiBot.url')}</label>
            <input id="aibot-url" className="uh-input" type="url" required
              placeholder={isOpenAI ? 'https://bot.example.com/v1' : 'https://bot.example.com/api/chat'}
              value={form.url} onChange={e => set('url', e.target.value)} />
          </div>

          {isOpenAI ? (
            <div className="uh-form-group">
              <label className="uh-label" htmlFor="aibot-model">{t('aiBot.model')}</label>
              <input id="aibot-model" className="uh-input" type="text" required
                value={form.model} onChange={e => set('model', e.target.value)} />
            </div>
          ) : (
            <>
              <div className="uh-form-group">
                <label className="uh-label" htmlFor="aibot-body">{t('aiBot.bodyTemplate')}</label>
                <textarea id="aibot-body" className="uh-input" rows={3} required style={{ fontFamily: 'monospace' }}
                  value={form.body_template} onChange={e => set('body_template', e.target.value)} />
                <div style={hintStyle}>{t('aiBot.bodyTemplateHint', PLACEHOLDERS)}</div>
              </div>
              <div className="uh-row-2">
                <div className="uh-form-group">
                  <label className="uh-label" htmlFor="aibot-path">{t('aiBot.answerPath')}</label>
                  <input id="aibot-path" className="uh-input" type="text" required placeholder="data.answer"
                    value={form.answer_path} onChange={e => set('answer_path', e.target.value)} />
                </div>
                <div className="uh-form-group">
                  <label className="uh-label" htmlFor="aibot-header">{t('aiBot.authHeader')}</label>
                  <input id="aibot-header" className="uh-input" type="text"
                    value={form.auth_header} onChange={e => set('auth_header', e.target.value)} />
                </div>
              </div>
            </>
          )}

          <div className="uh-form-group">
            <label className="uh-label" htmlFor="aibot-key">{t('aiBot.key')}</label>
            <input id="aibot-key" className="uh-input" type="password" autoComplete="off"
              placeholder={editing && check.ai_bot_key ? check.ai_bot_key : ''}
              value={form.key} onChange={e => set('key', e.target.value)} />
            <div style={hintStyle}>{editing && check.ai_bot_key ? t('aiBot.keyKeepHint') : t('aiBot.keyHint')}</div>
          </div>

          <div className="uh-form-group">
            <label className="uh-label" htmlFor="aibot-question">{t('aiBot.question')}</label>
            <input id="aibot-question" className="uh-input" type="text" required maxLength={QUESTION_MAX}
              placeholder={t('aiBot.questionPlaceholder')}
              value={form.question} onChange={e => set('question', e.target.value)} />
            <div style={hintStyle}>{t('aiBot.questionHint')}</div>
          </div>

          <div className="uh-form-group">
            <label className="uh-label" htmlFor="aibot-instruction">{t('aiBot.instruction')}</label>
            <textarea id="aibot-instruction" className="uh-input" rows={3} required maxLength={INSTRUCTION_MAX}
              placeholder={t('aiBot.instructionPlaceholder')}
              value={form.instruction} onChange={e => set('instruction', e.target.value)} />
            <div style={hintStyle}>{form.instruction.length}/{INSTRUCTION_MAX}</div>
          </div>

          <div className="uh-form-group">
            <label className="uh-label" htmlFor="aibot-interval">{t('projectDetail.checkFields.checkInterval')}</label>
            <select id="aibot-interval" className="uh-select" value={form.interval} onChange={e => set('interval', Number(e.target.value))}>
              {AI_BOT_INTERVALS.map(i => <option key={i.ns} value={i.ns}>{t(i.labelKey)}</option>)}
            </select>
            <div style={hintStyle}>{t('aiBot.intervalHint')}</div>
          </div>

          {result && (
            <div className={result.ok ? 'uh-alert-success' : 'uh-alert-error'} data-testid="aibot-result">
              <div><strong>{!result.ok ? t('aiBot.result.fail') : result.judged ? t('aiBot.result.ok') : t('aiBot.result.answered')}</strong></div>
              {result.failure && <div>{result.failure}</div>}
              {result.answer && <div style={{ marginTop: 6, whiteSpace: 'pre-wrap' }}>{t('aiBot.result.answer')}: {result.answer}</div>}
              {result.judged && result.reason && <div style={{ marginTop: 6 }}>{t('aiBot.result.reason')}: {result.reason}</div>}
              {!result.judged && result.answer && <div style={{ marginTop: 6 }}>{t('aiBot.result.notJudged')}</div>}
            </div>
          )}

          <div className="uh-modal-footer">
            <button type="button" className="uh-btn" onClick={onClose}>{t('common.cancel')}</button>
            <button type="button" className="uh-btn" onClick={handleTest} disabled={testing || saving}>
              {testing ? t('aiBot.testing') : t('aiBot.test')}
            </button>
            <button type="submit" className="uh-btn uh-btn-primary" disabled={saving || testing}>
              {saving ? t('common.saving') : t('common.save')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default AIBotModal;
